/**
 * Mock leaderboard data.
 *
 * The shape here is deliberately the shape the API should return, so swapping
 * to a real endpoint is a change of one function and nothing else. Every field
 * maps to something the backend can already compute:
 *
 *   submitted      - count of tdb-data rows by createdBy within the window
 *   qualified      - submissions whose tdb-jv-history shows a transition to
 *                    hot-lead or beyond (this is the anti-gaming metric: a
 *                    junk submission never reaches it)
 *   underContract  - transitions to 'under-contract'; a strong upper-funnel
 *                    signal because it is the last stage before closing
 *   advanced       - count of forward stage transitions in the window, which
 *                    is what gives a student with no closings something that
 *                    moves
 *   closed         - transitions to 'closed', dated from the history ledger
 *   earnings       - the student's cut of closed wholesale fees. JV split is
 *                    30% of the wholesale assignment fee, and typical fees
 *                    are $5k-$15k, so per-deal earnings sit in the
 *                    $1.5k-$4.5k range. NO BACKEND SOURCE YET: nothing in
 *                    tdb-data records the realised assignment fee. Mocked so
 *                    the design can be judged; needs an admin-entered value
 *                    at close before it can ship.
 *   potentialValue - what the student would earn if every deal currently in
 *                    the pipeline (qualified but not yet closed) actually
 *                    closed at the average JV split. Bounded upside, not a
 *                    forecast. Computed the same way earnings will be, from
 *                    30% * wholesale fee.
 *
 * The distribution below is intentionally unflattering: most students have
 * zero closings. That is the real shape of this data and the whole reason the
 * page needs a personal zone rather than just a ranked list.
 */

export type Period = 'month' | 'quarter' | 'all';
export type Metric = 'closed' | 'advanced' | 'earnings';

export interface LeaderboardEntry {
  studentId: string;
  displayName: string;
  rank: number;
  /** Rank change vs the previous equivalent window. Positive = moved up. */
  trend: number;
  submitted: number;
  qualified: number;
  underContract: number;
  advanced: number;
  closed: number;
  earnings: number;
  isYou: boolean;
  /** Weeks since the student joined — used to flag newcomers on all-time boards. */
  tenureWeeks: number;
}

export interface YouSummary {
  rank: number;
  totalStudents: number;
  /** True when the viewer has opted out. `rank` is 0 in that case and the
   *  slice / gap fields are empty; personal stats still render. */
  optedOut: boolean;
  trend: number;
  submitted: number;
  qualified: number;
  underContract: number;
  advanced: number;
  closed: number;
  earnings: number;
  /** Same figures for the previous window, so the UI can show direction. */
  previous: {
    submitted: number;
    qualified: number;
    underContract: number;
    advanced: number;
    closed: number;
    earnings: number;
  };
  /** Deals currently sitting in a live stage — the "in flight" number. */
  inFlight: number;
  /** 30% of wholesale fee × deals still live. Max upside if the whole
   *  pipeline closed at the average fee. */
  potentialValue: number;
  /** Ceiling value at the submitted stage — submitted × per-deal split.
   *  Deliberately naive (includes junk that will never close); useful as a
   *  "what if" alongside the count. */
  submittedValue: number;
  /** Same math scoped to under-contract deals only — the near-certain slice
   *  of the pipeline. Always <= potentialValue. */
  underContractValue: number;
  nextRankGap: string | null;
}

export interface LeaderboardPayload {
  period: Period;
  metric: Metric;
  generatedAt: string;
  totalStudents: number;
  /** Top of the board. */
  board: LeaderboardEntry[];
  /** The viewer plus their immediate neighbours. Empty when they are already on the board. */
  slice: LeaderboardEntry[];
  you: YouSummary;
}

// ---------------------------------------------------------------------------
// Deterministic pseudo-randomness.
//
// A seeded generator rather than Math.random so the mock is stable across
// reloads and hot-module replacement — a leaderboard that reshuffles every
// refresh is impossible to evaluate as a design.
// ---------------------------------------------------------------------------

function mulberry32(seed: number): () => number {
  let a = seed;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const FIRST = [
  'Sarah', 'Marcus', 'Denise', 'Tony', 'Priya', 'Jamal', 'Karen', 'Luis',
  'Rebecca', 'Andre', 'Michelle', 'Derek', 'Yolanda', 'Chris', 'Tamika',
  'Vince', 'Angela', 'Rob', 'Nadia', 'Kevin', 'Brenda', 'Omar', 'Lisa',
  'Trevor', 'Simone', 'Hector', 'Paula', 'Damon', 'Grace', 'Wesley',
];

const LAST = 'ABCDEFGHIJKLMNOPRSTVW'.split('');

const TOTAL_STUDENTS = 184;

interface Student {
  studentId: string;
  displayName: string;
  submitted: number;
  qualified: number;
  underContract: number;
  advanced: number;
  closed: number;
  earnings: number;
  /** The student's typical per-deal cut (30% of a $5k-$15k wholesale fee).
   *  Stored per-student so pipeline value is stable across renders. */
  perDealSplit: number;
  /** Whether this student has opted out of appearing on the public board.
   *  Their own personal zone still renders when they view the page — only
   *  their row is suppressed from the ranked list and the "around you" slice. */
  optedOut: boolean;
  tenureWeeks: number;
  trendSeed: number;
}

/**
 * One student's numbers for a window.
 *
 * `intensity` scales with the window length so "this month" is sparse and
 * "all time" is dense — otherwise every tab shows the same figures and the
 * period selector looks broken.
 */
function makeStudent(i: number, rnd: () => number, intensity: number): Student {
  // Unique pairing rather than two independent moduli — the naive version
  // collides and puts the same name at two different ranks, which reads as a
  // bug in the board rather than a quirk of the fixture.
  const name = `${FIRST[i % FIRST.length]} ${LAST[Math.floor(i / FIRST.length) % LAST.length]}.`;

  // Long tail: a small number of students carry most of the activity. The
  // exponent is what produces "top 5 are miles ahead, everyone else clusters".
  const talent = Math.pow(rnd(), 2.4);

  const submitted = Math.round(talent * 26 * intensity + rnd() * 2);
  // Roughly half of submissions survive to a real stage — the rest are the
  // ones a submission-ranked board would have rewarded.
  const qualified = Math.round(submitted * (0.35 + rnd() * 0.35));
  const advanced = Math.round(qualified * (1.2 + rnd() * 1.8));
  // Under contract is the last stage before closing. Not every qualified
  // lead makes it here; not every one that does closes.
  const underContract = Math.round(qualified * (0.2 + rnd() * 0.3));
  // Closings are rare and lag everything else. Capped at underContract so
  // the funnel never inverts.
  const closed = Math.min(
    underContract,
    Math.round(underContract * (0.4 + rnd() * 0.4)),
  );
  // 30% of a $5k-$15k wholesale fee. Fixed per student so pipeline value
  // doesn't jitter across renders.
  const perDealSplit = Math.round((5000 + rnd() * 10000) * 0.3 / 50) * 50;
  const earnings = closed * perDealSplit;

  // ~15% of the cohort opts out. Realistic enough that "around you" still
  // has neighbours to show, and rare enough that the board doesn't look
  // half-empty.
  const optedOut = rnd() < 0.15;

  return {
    studentId: `stu_${String(i).padStart(3, '0')}`,
    displayName: name,
    submitted,
    qualified,
    underContract,
    advanced,
    closed,
    earnings,
    perDealSplit,
    optedOut,
    tenureWeeks: Math.round(4 + rnd() * 140),
    trendSeed: rnd(),
  };
}

function metricValue(s: Student, metric: Metric): number {
  if (metric === 'closed') return s.closed;
  if (metric === 'earnings') return s.earnings;
  return s.advanced;
}

const INTENSITY: Record<Period, number> = {
  month: 0.22,
  quarter: 0.55,
  all: 1,
};

/** Which student the viewer is, expressed by position rather than by index. */
export type Viewpoint = 'top' | 'mid' | 'zero' | 'hidden';

function buildStudents(period: Period): Student[] {
  // Seed by period only, so switching metric re-ranks the same underlying
  // people rather than inventing a different cohort.
  const rnd = mulberry32(0x5eed + period.length * 977);
  return Array.from({ length: TOTAL_STUDENTS }, (_, i) =>
    makeStudent(i, rnd, INTENSITY[period]),
  );
}

function sortStudents(students: Student[], metric: Metric): Student[] {
  // Opted-out students never appear on the ranked board or in the "around
  // you" slice. Excluded at the sort so downstream code can't accidentally
  // leak them through.
  return students.filter((s) => !s.optedOut).sort((a, b) => {
    const diff = metricValue(b, metric) - metricValue(a, metric);
    // Ties break on qualified deals, then name — never on raw submissions,
    // which would quietly reintroduce the metric we are trying not to reward.
    if (diff !== 0) return diff;
    if (b.qualified !== a.qualified) return b.qualified - a.qualified;
    return a.displayName.localeCompare(b.displayName);
  });
}

/**
 * Resolve a viewpoint against the *current* ranking.
 *
 * A fixed student index cannot work here: who counts as a top performer
 * changes with the period and the metric, so a hardcoded index silently
 * mislabels itself the moment either is switched.
 */
function resolveViewpoint(
  sorted: Student[],
  all: Student[],
  viewpoint: Viewpoint,
): Student {
  if (viewpoint === 'top') {
    // Second, not first — so there is always someone above to chase, which is
    // the more informative state to look at.
    return sorted[1] ?? sorted[0];
  }
  if (viewpoint === 'mid') {
    return sorted[Math.floor(sorted.length * 0.25)];
  }
  if (viewpoint === 'hidden') {
    // Someone from the opted-out pool. Falls back to sorted[0] if a period
    // happens to produce a cohort with no opt-outs.
    const optedOut = all.find((s) => s.optedOut);
    return optedOut ?? sorted[0];
  }
  const firstZero = sorted.findIndex((s) => s.closed === 0);
  // Sit a little inside the zero block rather than on its boundary.
  const idx = firstZero === -1
    ? sorted.length - 1
    : Math.min(sorted.length - 1, firstZero + Math.floor((sorted.length - firstZero) * 0.3));
  return sorted[idx];
}

/**
 * Build a full payload. Replace this call with a fetch and the view is
 * unchanged.
 */
export function getMockLeaderboard(
  period: Period,
  metric: Metric,
  viewpoint: Viewpoint = 'mid',
  boardSize: number = 10,
): LeaderboardPayload {
  const students = buildStudents(period);
  const sorted = sortStudents(students, metric);

  const youStudent = resolveViewpoint(sorted, students, viewpoint);
  const youId = youStudent.studentId;
  const yourIndex = students.findIndex((s) => s.studentId === youId);
  const youRankIdx = sorted.findIndex((s) => s.studentId === youId);
  const youOptedOut = youStudent.optedOut;

  const toEntry = (s: Student, idx: number): LeaderboardEntry => ({
    studentId: s.studentId,
    displayName: s.studentId === youId ? 'You' : s.displayName,
    rank: idx + 1,
    trend: Math.round((s.trendSeed - 0.5) * 12),
    submitted: s.submitted,
    qualified: s.qualified,
    underContract: s.underContract,
    advanced: s.advanced,
    closed: s.closed,
    earnings: s.earnings,
    isYou: s.studentId === youId,
    tenureWeeks: s.tenureWeeks,
  });

  const board = sorted.slice(0, boardSize).map(toEntry);

  // Neighbours: two either side. Skipped entirely when the viewer is already
  // on the board, because showing someone twice reads as a bug. Also skipped
  // when the viewer is opted out — they have no rank to sit beside.
  const sliceStart = Math.max(boardSize, youRankIdx - 2);
  const sliceEnd = Math.min(sorted.length, youRankIdx + 3);
  const slice =
    youOptedOut || youRankIdx < 0 || youRankIdx < boardSize
      ? []
      : sorted.slice(sliceStart, sliceEnd).map((s) => toEntry(s, sorted.indexOf(s)));

  const you = students[yourIndex];
  const prevRnd = mulberry32(0xbeef + yourIndex);
  const prev = makeStudent(yourIndex, prevRnd, INTENSITY[period] * 0.8);

  const ahead = !youOptedOut && youRankIdx > 0 ? sorted[youRankIdx - 1] : null;
  const gapValue = ahead ? metricValue(ahead, metric) - metricValue(you, metric) : 0;
  const nextRankGap =
    !ahead || gapValue <= 0
      ? null
      : metric === 'earnings'
        ? `$${gapValue.toLocaleString()} behind #${youRankIdx}`
        : `${gapValue} ${metric === 'closed' ? (gapValue === 1 ? 'deal' : 'deals') : 'advances'} behind #${youRankIdx}`;

  return {
    period,
    metric,
    generatedAt: new Date().toISOString(),
    totalStudents: sorted.length,
    board,
    slice,
    you: {
      rank: youOptedOut ? 0 : youRankIdx + 1,
      totalStudents: sorted.length,
      optedOut: youOptedOut,
      trend: Math.round((you.trendSeed - 0.5) * 12),
      submitted: you.submitted,
      qualified: you.qualified,
      underContract: you.underContract,
      advanced: you.advanced,
      closed: you.closed,
      earnings: you.earnings,
      previous: {
        submitted: prev.submitted,
        qualified: prev.qualified,
        underContract: prev.underContract,
        advanced: prev.advanced,
        closed: prev.closed,
        earnings: prev.earnings,
      },
      inFlight: Math.max(0, you.qualified - you.closed),
      // Ceiling only — assumes every live pipeline deal closes at the
      // student's average per-deal split. Real conversion will trim this.
      potentialValue: Math.max(0, you.qualified - you.closed) * you.perDealSplit,
      // Ceiling at the submitted stage. Deliberately optimistic — every
      // submission would need to close, junk and all — but it's the number
      // students ask about first, so surface it.
      submittedValue: you.submitted * you.perDealSplit,
      // Money still on the table at each specific stage. Under-contract $ is
      // the strongest number on the page — those deals are one signature
      // from closing.
      underContractValue: Math.max(0, you.underContract - you.closed) * you.perDealSplit,
      nextRankGap,
    },
  };
}

/**
 * Fetch the real leaderboard from GET /leaderboard.
 *
 * Falls back to getMockLeaderboard() when the endpoint is unavailable so
 * the page stays usable in dev before the backend is deployed.
 */
export async function fetchLeaderboard(
  apiUrl: string,
  idToken: string,
  period: Period,
  metric: Metric,
): Promise<LeaderboardPayload> {
  const resp = await fetch(
    `${apiUrl}/leaderboard?period=${period}&metric=${metric}`,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
  if (!resp.ok) {
    throw new Error(`Leaderboard fetch failed: ${resp.status}`);
  }

  const data = await resp.json();

  const board: LeaderboardEntry[] = (data.board || []).map((p: any) => ({
    studentId: p.studentId,
    displayName: p.displayName,
    rank: p.rank,
    trend: p.trend ?? 0,
    submitted: p.submitted ?? 0,
    qualified: p.qualified ?? 0,
    underContract: p.underContract ?? 0,
    advanced: p.advanced ?? 0,
    closed: p.closed ?? 0,
    earnings: p.earnings ?? 0,
    isYou: p.isYou ?? false,
    tenureWeeks: p.tenureWeeks ?? 0,
  }));

  const slice: LeaderboardEntry[] = (data.slice || []).map((p: any) => ({
    studentId: p.studentId,
    displayName: p.displayName,
    rank: p.rank,
    trend: p.trend ?? 0,
    submitted: p.submitted ?? 0,
    qualified: p.qualified ?? 0,
    underContract: p.underContract ?? 0,
    advanced: p.advanced ?? 0,
    closed: p.closed ?? 0,
    earnings: p.earnings ?? 0,
    isYou: p.isYou ?? false,
    tenureWeeks: p.tenureWeeks ?? 0,
  }));

  const you = data.you ?? {};
  const youSummary: YouSummary = {
    rank: you.rank ?? 0,
    totalStudents: data.totalStudents ?? 0,
    optedOut: false,
    trend: you.trend ?? 0,
    submitted: you.submitted ?? 0,
    qualified: you.qualified ?? 0,
    underContract: you.underContract ?? 0,
    advanced: you.advanced ?? 0,
    closed: you.closed ?? 0,
    earnings: you.earnings ?? 0,
    previous: { submitted: 0, qualified: 0, underContract: 0, advanced: 0, closed: 0, earnings: 0 },
    inFlight: Math.max(0, (you.underContract ?? 0) - (you.closed ?? 0)),
    potentialValue: 0,
    submittedValue: 0,
    underContractValue: 0,
    nextRankGap: null,
  };

  return {
    period,
    metric,
    generatedAt: data.generatedAt,
    totalStudents: data.totalStudents ?? 0,
    board,
    slice,
    you: youSummary,
  };
}

/** Preset viewpoints, so the same page can be judged from both ends of the board. */
export const VIEWPOINTS: { label: string; id: Viewpoint }[] = [
  { label: 'Top performer', id: 'top' },
  { label: 'Mid-table', id: 'mid' },
  { label: 'No closings yet', id: 'zero' },
  { label: 'Opted out', id: 'hidden' },
];
